<template>
  <span>
    <div class="page-level-functions">
      <router-link to="/users/new">
        <action-button
          v-if="canAddUser"
          text="Añadir"
          i="fas fa-plus-square"
          variant="success float-right ml-3"
        />
      </router-link>

      <action-button
        v-if="canChangeRoleTeacher"
        @click="changeRoleTeacher()"
        variant="outline-success float-right ml-3"
        :text="getDisplayNameChangeTeacherRoleAction"
        i="fas fa-exchange-alt"
      />

      <action-button
        @click="back()"
        variant="outline-success float-right ml-3"
        text="Regresar"
        i="fas fa-arrow-circle-left"
      />
    </div>

    <hr class="page-level-functions-separator" />

    <div class="sub-content-wrapper">
      <search-criteria-entry @searchClicked="onClickSearchHandler">
        <div class="col-md-3 col-sm-12">
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text input-icon">
                <em class="fas fa-lg fa-search" />
              </span>
            </div>
            <input
              ref="filterInput"
              type="text"
              class="form-control filter-input"
              :placeholder="inputText.placeholder"
              :maxlength="inputText.maxlength"
              v-model="inputText.model"
            />
          </div>
        </div>
        <div class="col-md-3 col-sm-12 mt-2 mt-md-0 mt-sm-2">
          <custom-drop-down
            fieldkey="id"
            fieldname="name"
            @input="onChangedApplicationsHandler"
            :placeholder="ddlApplications.placeholder"
            :options="ddlApplications.options"
            v-model="ddlApplications.model"
          />
        </div>
        <div class="col-md-3 col-sm-12 mt-2 mt-md-0 mt-sm-2">
          <custom-drop-down
            fieldkey="roleId"
            fieldname="roleName"
            :disabled="ddlRoles.disabled"
            :placeholder="ddlRoles.placeholder"
            :options="ddlRoles.options"
            v-model="ddlRoles.model"
          />
        </div>
        <div class="col-md-2 col-sm-12">
          <custom-drop-down
            fieldkey="value"
            fieldname="name"
            :placeholder="ddlEstatus.placeholder"
            :options="userStatusOptions"
            :clearable="false"
            v-model="ddlEstatus.model"
          />
        </div>
        <div class="col-md-1 d-flex justify-content-center align-items-center">
          <div
            class="btn btn-sm px-3 py-2 col-md-1 d-flex align-items-center justify-content-center mt-1"
            :class="{ 'advanced-button': advancedFilter }"
            @click="onOpenAdvanceFilterHandler"
          >
            <i class="fas fa-sliders" />
          </div>
        </div>
        <div class="col-md-12" v-if="advancedFilter">
          <div class="row mt-3">
            <div class="col-md-4">
              <custom-input
                id="userName"
                v-model="userName"
                label="Nombre:"
                maxlength="64"
              />
            </div>

            <div class="col-md-1">
              <custom-input
                id="userInitial"
                v-model="userInitial"
                label="Inicial:"
                maxlength="64"
              />
            </div>
            
            <div class="col-md-3">
              <custom-input
                id="userPaternalSurname"
                v-model="userPaternalSurname"
                label="Apellido Paterno:"
                maxlength="64"
              />
            </div>

            <div class="col-md-3">
              <custom-input
                id="userMaternalSurname"
                v-model="userMaternalSurname"
                label="Apellido Materno:"
                maxlength="64"
              />
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-md-3">
              <custom-drop-down
                label="Nivel:"
                placeholder="Seleccione"
                :options="ddlLevels.options"
                fieldkey="id"
                fieldname="displayName"
                v-model="ddlLevels.model"
                @input="onChangeLevelHandler"
              />
            </div>
            <div class="col-md-3" v-if="showLocationSchoolFilters">
              <custom-drop-down
                :label="'Región'"
                placeholder="Seleccione"
                :options="regionOptions"
                fieldkey="id"
                fieldname="name"
                v-model="selectedRegion"
                @input="onChangeRegion"
              />
            </div>
            <div class="col-md-3" v-if="showLocationSchoolFilters">
              <custom-drop-down
                :label="'Municipio'"
                placeholder="Seleccione"
                :options="municipalityOptions"
                fieldkey="id"
                fieldname="name"
                v-model="selectedMunicipality"
                @input="onChangeMunicipality"
              />
            </div>
            <div class="col-md-3" v-if="showLocationSchoolFilters">
              <custom-drop-down
                :label="'Escuela'"
                placeholder="Seleccione"
                :options="schoolOptions"
                fieldkey="id"
                fieldname="name"
                v-model="selectedSchool"
              />
            </div>
            <div
              class="col-6"
              v-if="ddlLevels.model && !showLocationSchoolFilters"
            >
              <custom-drop-down
                :label="`${getDisplayLevelName(ddlLevels.model.name)}:`"
                placeholder="Seleccione"
                :options="ddlCenters.options"
                fieldkey="id"
                fieldname="name"
                v-model="ddlCenters.model"
              />
            </div>
          </div>
        </div>
      </search-criteria-entry>

      <div class="row mt-3">
        <custom-title-divider
          title="Información de los Usuarios"
        ></custom-title-divider>
      </div>

      <div class="mx-n3">
        <result-not-found v-if="result.data.length === 0" />

        <div v-for="(row, index) in result.data" :key="`row-app-${index}`">
          <callout class="my-3" :has-toggle="false">
            <div class="row my-0">
              <div class="col-md-10">
                <div class="row mt-2">
                  <div class="col-md-1 d-flex align-items-center mr-n4">
                    <em class="fas fa-2x fa-cogs align-middle row-icon" />
                  </div>
                  <div class="col-md-3">
                    <div class="color-secondary-6 font-16">Nombre</div>
                    <div class="color-black font-16 font-weight-normal">
                      {{ row.fullname }}
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="color-secondary-6 font-16">Usuario</div>
                    <div class="color-black font-16 font-weight-normal">
                      {{ row.username }}
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="color-secondary-6 font-16">Email</div>
                    <div class="color-black font-16 font-weight-normal word-break">
                      {{ email(row.email) }}
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="color-secondary-6 font-16">Rol(es)</div>
                    <div class="color-black font-16 font-weight-normal">
                      {{ row.rolenames }}
                    </div>
                  </div>
                  <div class="col-md-2 w-sm-40">
                    <div
                      class="activity-indicator"
                      :class="[row.isActive ? 'active' : 'inactive']"
                    >
                      {{ row.isActive ? 'Activo' : 'Inactivo' }}
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="col-md-2 d-flex justify-content-end align-items-center pr-5"
              >
                <action-button
                  @click="viewUser(`/users/${row.userId}`)"
                  variant="outline-success enter-button mr-2"
                  text="Entrar"
                  i="fas fa-sign-in-alt"
                  min-width="100px"
                />
              </div>
            </div>
          </callout>
        </div>
        <pagination
          v-if="showPagination"
          :show-text="true"
          @pagination-go-page="goToPageHandler"
          :data="result.pagination"
        />
      </div>
    </div>
  </span>
</template>

<script>
import SearchCriteriaEntry from '@/components/common/SearchCriteriaEntry';
import { getRolesByApplicattionId } from '@/modules/roles/api';
import { getUsersSearchResults, existsUserRoleSummerTeacher, changeUserRoleTeacher } from '@/modules/users/api';
import InterfacesServices from '@/modules/interfaces/api';
import { getApplicationsList } from '@/modules/applications/api';
import CustomTitleDivider from '@/components/common/CustomTitleDivider';
import { ActionButton } from '@/components/common';
import {} from '@/mixins/vue-alerts';
import { mapGetters, mapActions } from 'vuex';
import { name as usersModuleName } from '@/modules/users/store';
import Callout from '@/components/common/callout/View';
import ResultNotFound from '@/components/common/ResultNotFound';
import Pagination from 'Pagination';
import CustomDropDown from '@/components/common/form/CustomDropDown';
import locationAccessLevels from '@/utils/constants/locationAccessLevels.js';
import eduespService from '@/services/eduesp';
import storeMixin from '@/mixins/storeGenerator';
import CustomInput from 'CustomInput';

const initialState = () => ({
  inputText: {
    placeholder: 'Escriba Usuario, Nombre, Email o SIE',
    maxlength: 100,
    model: null,
  },
  ddlApplications: {
    placeholder: 'Seleccione Aplicación',
    options: [],
    model: null,
  },
  ddlRoles: {
    placeholder: 'Seleccione Rol',
    disabled: true,
    options: [],
    model: null,
  },
  ddlEstatus: {
    placeholder: 'Seleccione el Estatus',
    disabled: false,
    options: [],
    model: {
      //set default value
      value: null,
      name: 'Todos',
    },
  },
  ddlLevels: {
    placeholder: 'Seleccione el Nivel',
    disabled: false,
    options: [],
    model: null,
  },
  ddlCenters: {
    placeholder: 'Seleccione el Centro',
    disabled: false,
    options: [],
    model: null,
  },
  advancedFilter: null,
  result: {
    data: [],
    pagination: {
      totalItemCount: 0,
      count: 15,
      pageCount: 0,
      currentPage: 1,
    },
  },
  regionOptions: [],
  selectedRegion: null,
  municipalityOptions: [],
  selectedMunicipality: null,
  schoolOptions: [],
  selectedSchool: null,
  existsUserRoleSummerTeacher: false,
  userName: '',
  userInitial: '',
  userPaternalSurname: '',
  userMaternalSurname: '',
});

export default {
  name: 'UserSearch',
  components: {
    SearchCriteriaEntry,
    CustomTitleDivider,
    ActionButton,
    Callout,
    ResultNotFound,
    Pagination,
    CustomDropDown,
    CustomInput,
  },
  mixins: [storeMixin],
  data() {
    return {
      ...initialState(),
    };
  },
  computed: {
    ...mapGetters(usersModuleName, ['payload']),

    showPagination() {
      return (
        this.result.data.length > 0 &&
        this.result.pagination.totalItemCount > this.result.pagination.count
      );
    },
    email() {
      return (email) => (email ? email : '-');
    },
    getDisplayLevelName() {
      return (name) => {
        if (name === locationAccessLevels.Municipality) return 'Municipio';
        else if (name === locationAccessLevels.Region) return 'Region';
        else if (name === locationAccessLevels.District) return 'Distrito';
        else if (name === locationAccessLevels.Center) return 'CSEE';
        else if (name === locationAccessLevels.School) return 'Escuela';
        else if (name === locationAccessLevels.Company) return 'Compañía';
        else if (name === locationAccessLevels.CompanySpecialist) return 'Compañía Especialista';
        else if (name === locationAccessLevels.Student) return 'Estudiante';
        else return '-';
      };
    },
    userStatusOptions() {
      return [
        { value: null, name: 'Todos' },
        { value: true, name: 'Activo' },
        { value: false, name: 'Inactivo' },
      ];
    },
    showLocationSchoolFilters() {
      return this.ddlLevels.model?.name === locationAccessLevels.School;
    },
    schoolsFilter() {
      return this.schoolOptions.length > 0
        ? this.schoolOptions.map((x) => parseInt(x.id)).join()
        : null;
    },
    canAddUser() {
      return this.userHasPermissions('security.user.add');
    },
    canChangeRoleTeacher(){
      return this.userHasPermissions('security.user.changeroleteacher');
    },
    getDisplayNameChangeTeacherRoleAction(){
      if (this.existsUserRoleSummerTeacher)
        return 'Cambiar Rol a Maestro';

      return 'Cambiar Rol a Maestro de Verano';
    }
  },
  destroyed() {
    this.persistData(['result'], true);
  },
  beforeRouteEnter(_, from, next) {
    next((vm) => {
      if (from.name == 'home') {
        vm.shouldRecovery = false;
        vm = {
          ...vm,
          ...initialState(),
        };
      } else vm.recoveryData();

      vm.callGetResult(vm.result.pagination.currentPage);
    });
  },
  async mounted() {
    const applications = await getApplicationsList();
    this.ddlApplications.options = applications;

    const { data: levels } = await InterfacesServices.getLevels();
    this.ddlLevels.options = levels?.map((x) => {
      x.displayName = this.getDisplayLevelName(x.name);
      return x;
    });

    await this.callGetResult(this.result.pagination.currentPage);

    if (this.affectedUserData) {
      const verb =
        this.affectedUserData.operation === 'I'
          ? 'creado'
          : this.affectedUserData.operation == 'U'
          ? 'actualizado'
          : '???'; //ECG:No se que le hiciste al user! :P
      this.Toast(
        'Aviso',
        'El usuario fue ' + verb + ' exitosamente.',
        'success'
      );
      await this.setAffectedUserData(null);
    }

    const { data } = await existsUserRoleSummerTeacher();
    this.existsUserRoleSummerTeacher = data;
  },
  methods: {
    ...mapActions(usersModuleName, [
      'setAffectedUserData',
      'setSearchCriteria',
    ]),

    async onChangedApplicationsHandler(app) {
      this.ddlRoles.model = null;
      this.ddlEstatus.model = {
        //set default value
        value: null,
        name: 'Todos',
      };

      if (!app) {
        this.ddlRoles.disabled = true;
        return;
      }

      this.ddlRoles.disabled = false;

      await this.getRoles(app.id);
    },

    async getRoles(appId) {
      await getRolesByApplicattionId(appId).then((res) => {
        const items = res.data;
        this.ddlRoles.options = items;

        if (items.length == 1) {
          this.ddlRoles.model = items[0];
        }
      });
    },

    onClickSearchHandler() {
      this.setSearchCriteria(null);
      this.callGetResult();
    },
    async callGetResult(pageNumber = 1, defaultActiveValue) {
      if (this.payload === null) {
        let searchValues = {
          filterCriteria: this.inputText.model ?? null,
          applicationId: this.ddlApplications.model?.id ?? null,
          roleId: this.ddlRoles.model?.roleId ?? null,
          isActive:
            defaultActiveValue || (this.ddlEstatus.model?.value ?? null),
          levelId: this.ddlLevels.model?.id ?? null,
          centerId: this.showLocationSchoolFilters
            ? this.selectedSchool?.id
            : this.ddlCenters.model?.id ?? null,
          schools: this.schoolsFilter,
          pageNumber,
          pageSize: this.result.pagination.count,
          userName: this.userName,
          userInitial: this.userInitial,
          userPaternalSurname: this.userPaternalSurname,
          userMaternalSurname: this.userMaternalSurname,
        };
        this.setSearchCriteria({
          ...searchValues,
          advancedFilter: this.advancedFilter,
          selectedRegionId: this.selectedRegion?.id,
          selectedMunicipalityId: this.selectedMunicipality?.id,
          selectedSchoolId: this.selectedSchool?.id,
        });
      } else {
        this.inputText.model = this.payload?.filterCriteria;
        this.advancedFilter = this.payload?.advancedFilter;
        this.result.pagination.count = this.payload?.pageSize;

        if (this.payload?.applicationId) {
          this.ddlApplications.model = this.ddlApplications.options.find(
            (app) => app.id === this.payload.applicationId
          );

          await this.getRoles(this.payload.applicationId);
          this.ddlRoles.disabled = false;

          if (this.payload?.roleId) {
            this.ddlRoles.model = this.ddlRoles.options.find(
              (role) => role.roleId === this.payload.roleId
            );
          }
        }

        if (this.payload?.levelId) {
          this.ddlLevels.model = this.ddlLevels.options.find(
            (item) => item.id === this.payload.levelId
          );
          if (this.showLocationSchoolFilters) {
            this.regionOptions = await eduespService.getRegions();
          } else {
            this.ddlCenters.options = (
              await InterfacesServices.getLevelCenters(
                this.ddlLevels.model?.name
              )
            ).data;
          }
          if (this.payload?.centerId) {
            this.ddlCenters.model = this.ddlCenters.options.find(
              (item) => item.id === this.payload.centerId
            );
          }

          if (this.payload?.selectedRegionId) {
            this.selectedRegion = this.regionOptions.find(
              (item) => item.id === this.payload.selectedRegionId
            );
            this.municipalityOptions = await eduespService.getMunicipalities({
              regionId: this.payload.selectedRegionId,
            });
            this.schoolOptions = await eduespService.getSchools({
              regionId: this.payload.selectedRegionId,
            });
          }
          if (this.payload?.selectedMunicipalityId) {
            this.selectedMunicipality = this.municipalityOptions.find(
              (item) => item.id === this.payload.selectedMunicipalityId
            );
          }
          if (this.payload?.selectedSchoolId) {
            this.selectedSchool = this.schoolOptions.find(
              (item) => item.id === this.payload.selectedSchoolId
            );
          }
        }
      }

      const { data } = await getUsersSearchResults(this.payload);

      this.result = {
        ...this.result,
        data: data.data,
        pagination: {
          ...this.result.pagination,
          currentPage: pageNumber,
          totalItemCount: data.totalItemCount,
          pageCount: data.pageCount,
        },
      };
    },
    goToPageHandler(page) {
      this.setSearchCriteria(null);
      this.callGetResult(page);
    },
    viewUser(path) {
      this.$router.push({ path });
    },
    back() {
      this.setSearchCriteria(null);
      let from =this.$route.params.from;
      if(from=="UserRequest"){
        this.$router.push({ name: from})
      }else{
            this.$router.push({ name: 'home'})
      }
    },
    async changeRoleTeacher(){
      try{
        await changeUserRoleTeacher();
        this.existsUserRoleSummerTeacher = !this.existsUserRoleSummerTeacher;
        this.Toast('Proceso exitoso', 'El proceso ha culminado de manera exitosa.', 'success');
        this.callGetResult();
      }
      catch(err)
      {
         this.Toast(
          'Error',
          'Ocurrio un error.',
          'error'
        )
      }
    },
    async onChangeLevelHandler(value) {
      this.ddlCenters.model = null;

      if (this.showLocationSchoolFilters) {
        this.regionOptions = await eduespService.getRegions();
        return;
      }

      if (value?.id) {
        let { data } = await InterfacesServices.getLevelCenters(value?.name);
        this.ddlCenters.options = data;
      }

      this.clearSchoolLocationFilters();
    },
    onOpenAdvanceFilterHandler() {
      this.advancedFilter = !this.advancedFilter;
      if (!this.advancedFilter) {
        this.ddlLevels.model = null;
        this.ddlCenters.model = null;
        this.userName = null;
        this.userInitial = null;
        this.userPaternalSurname = null;
        this.userMaternalSurname = null;
      }
    },
    async onChangeRegion(value) {
      if (!value) {
        this.municipalityOptions = [];
        this.schoolOptions = [];
        this.selectedMunicipality = null;
        this.selectedSchool = null;
        return;
      }
      this.selectedMunicipality = null;
      this.selectedSchool = null;
      this.municipalityOptions = await eduespService.getMunicipalities({
        regionId: value.id,
      });
      this.schoolOptions = await eduespService.getSchools({
        regionId: value.id,
      });
    },
    async onChangeMunicipality(value) {
      if (!value) {
        this.schoolOptions = [];
        this.onChangeRegion(this.selectedRegion);
        return;
      }

      this.selectedSchool = null;
      this.schoolOptions = await eduespService.getSchools({
        municipalityId: value.id,
      });
    },
    clearSchoolLocationFilters() {
      this.selectedRegion = null;
      this.selectedMunicipality = null;
      this.selectedSchool = null;
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/sass/config/_colors.scss';
.enter-button {
  background-color: transparent;
  float: right;
}

.word-break {
    -ms-word-break: break-all;
    word-break: break-all;
    word-break: break-word;
}

.row-icon {
  color: #ff7058;
}

.activity-indicator {
  border-radius: 5px;
  font: Bold 16px/28px Lato;
  padding: 6px 26px;
  text-align: center;
}

.activity-indicator.active {
  background-color: #d0ffd5;
  color: #297b31;
}

.activity-indicator.inactive {
  background-color: #B5C3BC;
  color: #3F5B60;
}

.field-label {
  font: 1rem 'Lato';
  letter-spacing: 0;
  display: block;
  color: #727272;
}

.field-value {
  font: 1rem 'Lato';
  letter-spacing: 0;
  color: #11291b;
}

/*** page level functions container **/
.page-level-functions {
  display: inline-block !important;
  vertical-align: bottom !important;
  width: 50%;
}

hr.page-level-functions-separator {
  margin: 20px -45px;
  border: 1px solid #e3e3e3;
  border-bottom: none;
}

/*** wrapper para alinear contenido bajo header de pagina **/
.sub-content-wrapper {
  padding: 0px 15px 0 15px;
}

.advanced-button {
  background-color: map-get($colors, 'orange');
  color: map-get($colors, 'white');
  border-color: map-get($colors, 'orange');
  &:hover {
    background-color: map-get($colors, 'orange');
    color: map-get($colors, 'white');
    border-color: map-get($colors, 'orange');
  }
}
</style>
